import { useEffect, useRef, useState } from "react";

import './Pagination.scss';

export const Pagination = ({ loading, page, onPageChangeCallback }) => {

    const [currentPage, setCurrentPage] = useState(1);
    const [pages, setPages] = useState([]);

    const pageDetail = useRef({ total: 0, size: 0 });

    useEffect(() => {
        if ( typeof page === 'undefined' ) {
            page = {};
        }

        let size = page.size ?? pageDetail.current.size;
        let total = page.total ?? pageDetail.current.total;

        if ( total && size ) {
            let pageList = [];
            let count = Math.ceil(total / size);
            
            const left = currentPage == count ? currentPage - 4 : currentPage - 2;
            const right = currentPage > 2 ? currentPage + 2 : currentPage + 4;

            for (let i = 1; i <= count;) {
                if (i === 1 || i === count || (i >= left && i <= right)) {
                    pageList.push(i);
                } else {
                    if (pageList[pageList.length - 1] !== null) {
                        pageList.push(null);
                    }
                }
                i++;
            }

            setPages(pageList);
            pageDetail.current = { total, size };
        } else {
            setPages([]);
            pageDetail.current = { total: 0, size: 0 };
        }
    }, [page, currentPage]);

    useEffect(() => {
        if ( page.currentpage ) {
            setCurrentPage(page.currentpage);
        }
    }, [page.currentpage]);

    const onChangePage = (previousPageNumber, currentPageNumber) => {
        if ( previousPageNumber !== currentPageNumber ) {
            setCurrentPage(currentPageNumber);
            const pagePayload = { "size": 10 };

            // Going to next page or previouse page.
            if (  (previousPageNumber + 1) === currentPageNumber ) {
                pagePayload.pagenumber = currentPageNumber;
            } else if ( (previousPageNumber - 1) === currentPageNumber ) {
                pagePayload.pagenumber = currentPageNumber;
            } else {
                // Page jump identified
                pagePayload.pagenumber = currentPageNumber;
            }
            onPageChangeCallback && onPageChangeCallback(pagePayload);
        }
    };

    return (
        <div className="w-100 d-flex align-items-center">
            <div className="w-50">
                {
                    !loading && pageDetail.current.total > 0 && (
                    <div style={{ fontSize: "12px" }}>
                        <span>Showing </span>
                        <b>{Math.min(((currentPage - 1) * pageDetail.current.size + 1), pageDetail.current.total)}</b> <span> - </span>
                        <b>{Math.min(currentPage * pageDetail.current.size, pageDetail.current.total)}</b> <span> of </span> 
                        <b>{pageDetail.current.total}</b> records
                    </div>
                    )
                }
            </div>
            <div className="pagination-box w-50 d-flex justify-content-end">
                {
                    loading ?  null : pages.length > 1 && 
                    pages.map((page) => (
                        page === null ? 
                        <div className="page-dots">
                            <img src={`${process.env.PUBLIC_URL}/static/icons/common-icons/dots.svg`} />
                        </div> :
                        <div role="button" className={`page-item ${page === currentPage ? 'active' : ''}`} onClick={() => { onChangePage(currentPage, page); }}>
                            {page}
                        </div>
                    ))
                }
            </div>
        </div>
    );
};

export const PaginationDots = ({ loading, pageSize, totalSize, scrollRef ,timerEvent=false}) => {
    const [dots, setDots] = useState(0);
    const [activeDotIndex, setActiveDotIndex] = useState(0);

    const maxDots = useRef(0);
    const paginationDotsRef = useRef(null);

    useEffect(() => {
        if ( pageSize > 0 && totalSize > 0 ) {
            const count = Math.ceil(totalSize / pageSize);
            setDots(count);
            maxDots.current = count;
        }
    }, [pageSize, totalSize]);

    useEffect(() => {
        if ( scrollRef && scrollRef.current ) {
            scrollRef.current.addEventListener('scroll', (event) => { handleScroll(event); });
            scrollRef.current.addEventListener('scrollend', (event) => { handleScroll(event); });
        }

        return () => {
            if ( scrollRef && scrollRef.current ) {
                scrollRef.current.removeEventListener('scroll', (event) => { handleScroll(event); });
                scrollRef.current.removeEventListener('scrollend', (event) => { handleScroll(event); });
            }
        };
    }, [scrollRef?.current]);

    const handleScroll = (event) => {
        if ( event.target.scrollLeft + event.target.clientWidth >= event.target.scrollWidth - 1 ) {
            setActiveDotIndex(maxDots.current - 1);
        } else {
            setActiveDotIndex(Math.round(scrollRef.current.scrollLeft / scrollRef.current.offsetWidth));
        }
    };

    const onChangePage = (index) => {
        if ( scrollRef.current ) {
            scrollRef.current.scrollTo({
                left: index * scrollRef.current.offsetWidth,
                behavior: 'smooth',
            });
        }
    };

    return (
        <div className={`pagination-dots-box ${paginationDotsRef.current?.scrollWidth > paginationDotsRef.current?.clientWidth ? '' : 'justify-content-center'}`} ref={paginationDotsRef}>
            {
                loading ? null :
                    dots > 1 && [...Array(dots)].map((elem, index) => <div key={`pagination-dot-${index}`} role="button" className={`dots ${activeDotIndex === index ? 'active' : ''}`} onClick={() => { onChangePage(index); }}></div>)
            }
        </div>
    );
};